/**
 * onwatchHostDisplay.cjs
 *
 * Phase 4a UI: pure display-logic helpers for agent-sourced host data
 * (GPU, disk mounts, hardware identity, agent tier status) on
 * OnWatchHealth.jsx and OnWatchInstances.jsx. Kept separate from the
 * components themselves (no JSX here) so this is directly requireable by
 * a plain Node test script - none of these functions touch the DOM,
 * fetch, or React state.
 *
 * .cjs rather than .js: package.json sets "type": "module", so a plain
 * .js file here would be ESM and unrequireable by a throwaway test
 * script. Vite/esbuild interop CommonJS .cjs files into ESM imports
 * automatically, so the JSX components import from this file normally.
 */

// ============================================================================
// Task B: disk mounts
// ============================================================================

const CLASSIFICATION_LABEL = {
  etcd: 'etcd datastore',
  logs: 'OnWatch logs',
};

/**
 * Orders and labels the disks JSON object for display. "/" always first
 * (it holds etcd and containerd's own state and is the documented upgrade
 * risk), then the remaining mounts largest-to-smallest (the data volumes
 * an operator actually cares about), with "/boot/efi" forced last
 * regardless of size (nobody cares until it is full). A mount the
 * collector classified (etcd, logs) gets that in its label; an
 * unclassified mount is shown by its path alone.
 */
function buildMountList(disks) {
  if (!disks || typeof disks !== 'object') return [];

  const entries = Object.entries(disks).map(([mountpoint, disk]) => ({
    mountpoint,
    source: disk?.source ?? null,
    percent: typeof disk?.percent === 'number' ? disk.percent : null,
    sizeBytes: typeof disk?.sizeBytes === 'number' ? disk.sizeBytes : null,
    availBytes: typeof disk?.availBytes === 'number' ? disk.availBytes : null,
    classification: disk?.classification || null,
    label: CLASSIFICATION_LABEL[disk?.classification]
      ? `${mountpoint} (${CLASSIFICATION_LABEL[disk.classification]})`
      : mountpoint,
  }));

  entries.sort((a, b) => {
    if (a.mountpoint === '/') return -1;
    if (b.mountpoint === '/') return 1;
    if (a.mountpoint === '/boot/efi') return 1;
    if (b.mountpoint === '/boot/efi') return -1;
    return (b.sizeBytes ?? 0) - (a.sizeBytes ?? 0);
  });

  return entries;
}

// ============================================================================
// Task A: GPU absence reason
// ============================================================================

/**
 * gpuUnknown is true for three structurally different reasons, and only
 * one of them is a fault. "not_bound" and "offline" are configuration and
 * outage states respectively (Task A's own distinction); "no_gpu" is
 * neither - the host agent is healthy and simply found no GPU on this
 * hardware, which is a legitimate and expected state on non-GPU nodes.
 */
function gpuUnavailableInfo({ hostBound, hostOnline }) {
  if (!hostBound) {
    return {
      reason: 'not_bound',
      message: 'No host agent bound. GPU, full disk and hardware detail require an agent installed on the appliance.',
    };
  }
  if (!hostOnline) {
    return {
      reason: 'offline',
      message: 'Host agent is offline. GPU telemetry unavailable until it reconnects.',
    };
  }
  return {
    reason: 'no_gpu',
    message: 'Host agent is online and reports no GPU on this hardware.',
  };
}

// ============================================================================
// Task C: hardware identity
// ============================================================================

const HARDWARE_FIELDS = [
  { key: 'hwVendor', label: 'Vendor' },
  { key: 'hwProductName', label: 'Model' },
  { key: 'hwBoardName', label: 'Board' },
  { key: 'hwProductSerial', label: 'Serial' },
  { key: 'hwChassisSerial', label: 'Chassis serial' },
  { key: 'hwBoardSerial', label: 'Board serial' },
];

/**
 * Blank DMI strings are common (Task C's own note) - only fields that
 * actually have a value are returned, never a row rendered as "Model:
 * none". Order matches HARDWARE_FIELDS, not object key order.
 */
function filterHardwareFields(node) {
  return HARDWARE_FIELDS
    .map(({ key, label }) => ({ key, label, value: node?.[key] }))
    .filter((f) => f.value != null && f.value !== '');
}

/**
 * Dell's warranty lookup URL format is verified (Part Three's own DMI
 * research); no other vendor's format has been. Fabricating a URL for an
 * unverified vendor is explicitly out - a copy-serial affordance is the
 * honest fallback for everyone else, and for Dell units with no serial at
 * all (the link would 404).
 */
function buildWarrantyLink({ vendor, serial }) {
  if (!serial) return null;
  if (vendor === 'Dell') {
    return { type: 'link', url: `https://www.dell.com/support/home/en-us/product-support/servicetag/${encodeURIComponent(serial)}/overview` };
  }
  return { type: 'copy', serial };
}

// ============================================================================
// Task D: agent tier status
// ============================================================================

/**
 * Mirrors the existing k8s-tier (`stale`) and app-tier (`appStale`/
 * `appConfigured`) derivations in OnWatchHealth.jsx exactly, as a third,
 * independent tier. bound=false is a configuration state (most instances
 * legitimately have no host agent), never rendered as a fault; bound=true
 * with stale=true IS a fault (agent was configured and has gone quiet).
 */
function deriveHostTierStatus(instance, defaultStalenessSec) {
  const bound = !!(instance && instance.hostAgentId);
  if (!bound) {
    return { bound: false, online: false, stale: false };
  }
  const online = instance.hostAgentStatus === 'ONLINE';
  const stalenessSec = instance.stalenessSec || defaultStalenessSec || 300;
  const lastSuccessMs = instance.hostCollectionLastSuccessAt ? new Date(instance.hostCollectionLastSuccessAt).getTime() : null;
  const ageSec = lastSuccessMs == null ? Infinity : (Date.now() - lastSuccessMs) / 1000;
  const stale = !online || ageSec > stalenessSec;
  return { bound: true, online, stale };
}

module.exports = {
  buildMountList,
  gpuUnavailableInfo,
  filterHardwareFields,
  buildWarrantyLink,
  deriveHostTierStatus,
};
